import { useCallback, useEffect, useState } from "react";
import { checkAccess } from "../apis/subscription";

// Module-level cache so every consumer (nav, gate, pages) shares one fetch.
let cached = null;
let inflight = null;
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn(cached));
}

async function fetchAccess(force = false) {
  if (cached && !force) return cached;
  if (inflight && !force) return inflight;
  inflight = checkAccess()
    .then((res) => {
      if (res?.data?.success) {
        cached = res.data.data;
      } else {
        cached = { hasAccess: false, exempt: false, subscription: null };
      }
      return cached;
    })
    .catch((err) => {
      // On network/server error, fail open so a hiccup doesn't lock the UI;
      // the backend still enforces on every mutating request.
      cached = { hasAccess: true, exempt: false, subscription: null, numberLimit: null, error: true };
      return cached;
    })
    .finally(() => {
      inflight = null;
      notify();
    });
  return inflight;
}

export function invalidateSubscriptionAccess() {
  cached = null;
}

export default function useSubscriptionAccess() {
  const [state, setState] = useState(cached);

  useEffect(() => {
    const listener = (value) => setState(value ? { ...value } : value);
    listeners.add(listener);
    if (!cached) {
      fetchAccess();
    }
    return () => listeners.delete(listener);
  }, []);

  const refresh = useCallback(async () => {
    await fetchAccess(true);
  }, []);

  return {
    loading: !state,
    hasAccess: state ? state.hasAccess : true,
    exempt: state ? state.exempt : false,
    subscription: state ? state.subscription : null,
    numberLimit: state ? state.numberLimit : null,
    refresh,
  };
}
